import React, { useMemo, useState } from 'react';
import { 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  Link as MuiLink
} from '@mui/material';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { filterUserAnswers, getAvailableFilters } from '../../helpers/dashboardFilters';
import FacebookIcon from '@material-ui/icons/Facebook';
import InstagramIcon from '@material-ui/icons/Instagram';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import TwitterIcon from '@material-ui/icons/Twitter';
import YouTubeIcon from '@material-ui/icons/YouTube';
import LanguageIcon from '@material-ui/icons/Language';
import Chip from '@mui/material/Chip';

const PLATFORM_ICONS = {
  facebook: <FacebookIcon color="primary" sx={{ mr: 1 }} />,
  instagram: <InstagramIcon color="primary" sx={{ mr: 1 }} />,
  linkedin: <LinkedInIcon color="primary" sx={{ mr: 1 }} />,
  twitter: <TwitterIcon color="primary" sx={{ mr: 1 }} />,
  youtube: <YouTubeIcon color="primary" sx={{ mr: 1 }} />,
  website: <LanguageIcon color="primary" sx={{ mr: 1 }} />,
};

function getPlatformFromText(text) {
  const lower = text.toLowerCase();
  if (lower.includes('facebook')) return 'facebook';
  if (lower.includes('instagram')) return 'instagram';
  if (lower.includes('linkedin')) return 'linkedin';
  if (lower.includes('twitter') || lower.includes('x (twitter')) return 'twitter';
  if (lower.includes('youtube')) return 'youtube';
  if (lower.includes('website')) return 'website';
  return null;
}

function extractUrl(text) {
  // Simple regex for URLs
  const urlMatch = text.match(/https?:\/\/[\w\-\.\/?=&%#:@]+/i);
  return urlMatch ? urlMatch[0] : null;
}

// Helper to extract href and text from an <a> tag
function extractHrefAndText(html) {
  const match = html.match(/<a [^>]*href=["']([^"']+)["'][^>]*>(.*?)<\/a>/i);
  if (match) {
    return { href: match[1], text: match[2] };
  }
  return null;
}

const DashboardDetails = ({ details, userType }) => {
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Resolve the subject type (whose details we are showing). Priority:
  // 1) explicit subjectType prop
  // 2) explicit userType prop (backward-compat)
  // 3) auto-detect from answers distribution
  const resolvedSubjectType = useMemo(() => {
    if (userType === 'VENDOR') return 'FOUNDER';
    if (userType === 'FOUNDER') return 'VENDOR';

    // Fallback: auto-detect from answers distribution if userType is missing
    const answers = details?.userAnswers || [];
    if (!Array.isArray(answers) || answers.length === 0) return 'FOUNDER';
    const founderFilters = Object.keys(getAvailableFilters('FOUNDER') || {});
    const vendorFilters = Object.keys(getAvailableFilters('VENDOR') || {});
    const founderCount = founderFilters.reduce((acc, key) => acc + filterUserAnswers(answers, 'FOUNDER', key).length, 0);
    const vendorCount = vendorFilters.reduce((acc, key) => acc + filterUserAnswers(answers, 'VENDOR', key).length, 0);
    if (founderCount === 0 && vendorCount === 0) return 'FOUNDER';
    return founderCount >= vendorCount ? 'FOUNDER' : 'VENDOR';
  }, [userType, details?.userAnswers]);

  const availableFilters = useMemo(() => getAvailableFilters(resolvedSubjectType), [resolvedSubjectType]);

  if (!details) {
    return <Typography>No details found.</Typography>;
  }

  return (
    <Box>
      {/* Basic Info Section */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>Basic Information</Typography>
        <List>
          <ListItem>
            <ListItemText primary="Service Name" secondary={details?.name || details?.title || '-'} />
          </ListItem>
          <ListItem>
            <ListItemText primary="Status" secondary={details?.status || '-'} />
          </ListItem>
          <ListItem>
              <ListItemText 
                primary="Email" 
                secondary={details?.creator?.email || '-'} 
              />
          </ListItem>
          {details.creator?.profile?.orgName && (
            <ListItem>
              <ListItemText primary="Company Name" secondary={details.creator.profile.orgName} />
            </ListItem>
          )}
          {(details.creator?.firstName || details.creator?.lastName) && (
            <ListItem>
              <ListItemText
                primary="Representative Name"
                secondary={[details.creator.firstName, details.creator.lastName].filter(Boolean).join(' ')}
              />
            </ListItem>
          )}
        </List>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Key Details Section */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>Key Details</Typography>
        {Object.entries(availableFilters || {}).map(([filterKey, filterValue]) => {
          const filteredAnswers = filterUserAnswers(details?.userAnswers, resolvedSubjectType, filterKey);

          // Special rendering for Web and Social Presence
          if (filterKey === 'Web and Social Presence') {
            // Filter out repetitive platform indication questions
            const filteredWebSocialAnswers = (filteredAnswers || []).filter(ua =>
              ua?.question?.question && !ua.question.question.toLowerCase().includes('please indicate which platforms you or your firm is using')
            );
            // Separate platform and non-platform entries
            const platformEntries = {};
            const nonPlatformEntries = [];
            filteredWebSocialAnswers.forEach((ua) => {
              const answer = (ua?.response || ua?.answer?.answer || '') ?? '';
              const platform = getPlatformFromText(((ua?.question?.question) || '') + ' ' + answer);
              const aTag = extractHrefAndText(answer);
              const url = extractUrl(answer);
              if (platform) {
                if (aTag || url) {
                  // Prefer link entry
                  platformEntries[platform] = { ...ua, link: aTag?.href || url, linkText: aTag?.text || (platform.charAt(0).toUpperCase() + platform.slice(1)), answer };
                } else if (!platformEntries[platform]) {
                  // Only use plain name if no link exists
                  platformEntries[platform] = { ...ua, link: null, linkText: platform.charAt(0).toUpperCase() + platform.slice(1), answer };
                }
              } else {
                // For non-platform questions (like website/social active), always include
                nonPlatformEntries.push({ ...ua, answer });
              }
            });
            return (
              <Accordion
                key={filterKey}
                expanded={expanded === filterKey}
                onChange={handleChange(filterKey)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: 'primary.light',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                    },
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {filterKey}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                    {/* Render non-platform entries first */}
                    {(nonPlatformEntries || []).map((entry) => {
                      const cleanQuestion = (entry?.question?.question || '').replace(/<[^>]+>/g, '');
                      return (
                        <ListItem key={entry.id}>
                          <ListItemText
                            primary={cleanQuestion}
                            secondary={entry.answer}
                          />
                        </ListItem>
                      );
                    })}
                    {/* Then render deduplicated platform entries */}
                    {Object.values(platformEntries || {}).map((entry) => {
                      const platform = getPlatformFromText(((entry?.question?.question) || '') + ' ' + (entry?.answer || ''));
                      const cleanQuestion = (entry?.question?.question || '').replace(/<[^>]+>/g, '');
                      return (
                        <ListItem key={entry.id}>
                          <ListItemText
                            primary={cleanQuestion}
                            secondary={
                              <span style={{ display: 'flex', alignItems: 'center' }}>
                                {platform && PLATFORM_ICONS[platform]}
                                {entry.link ? (
                                  <MuiLink href={entry.link} target="_blank" rel="noopener noreferrer">
                                    {platform ? (platform.charAt(0).toUpperCase() + platform.slice(1)) : entry.link}
                                  </MuiLink>
                                ) : (
                                  <span style={{ marginLeft: platform ? 4 : 0 }}>
                                    {platform ? (platform.charAt(0).toUpperCase() + platform.slice(1)) : ''}
                                  </span>
                                )}
                              </span>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </AccordionDetails>
              </Accordion>
            );
          }

          // Special rendering for Cost
          if (filterKey === 'Cost') {
            return (
              <Accordion
                key={filterKey}
                expanded={expanded === filterKey}
                onChange={handleChange(filterKey)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: 'primary.light',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                    },
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {filterKey}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List>
                  {(filteredAnswers || []).map((ua) => {
                      // Format payment methods
                      if (
                        ua?.question?.question &&
                        ua.question.question.toLowerCase().includes('payment') &&
                        (ua?.response || ua?.answer?.answer)
                      ) {
                        // Try to split by comma or slash or parenthesis
                        let methods = (ua?.response || ua?.answer?.answer || '').split(/[,/()]+/)
                          .map(m => m.trim())
                          .filter(Boolean);
                        // If only one method, just show as text
                        if (methods.length === 1) methods = null;
                        return (
                          <ListItem key={ua.id}>
                            <ListItemText
                              primary={ua.question.question}
                              secondary={methods ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                  {methods.map((method, idx) => (
                                    <Chip key={idx} label={method} size="small" />
                                  ))}
                                </Box>
                              ) : (
                                ua?.response || ua?.answer?.answer
                              )}
                            />
                          </ListItem>
                        );
                      }
                      // Format references JSON
                      if (
                        ua?.question?.question &&
                        ua.question.question.toLowerCase().includes('reference') &&
                        (ua?.response || ua?.answer?.answer)
                      ) {
                        let refs = null;
                        try {
                          refs = JSON.parse(ua?.response || ua?.answer?.answer);
                        } catch (e) {}
                        return (
                          <ListItem key={ua.id} alignItems="flex-start">
                            <ListItemText
                              primary={ua.question.question}
                              secondary={refs && Array.isArray(refs) ? (
                                <List dense>
                                  {refs.map((ref, idx) => (
                                    <ListItem key={idx} disableGutters>
                                      <Typography component="span" fontWeight="bold">{ref.name}</Typography>{' '}
                                      <Typography component="span" color="text.secondary">({ref.duration})</Typography>{' '}
                                      <MuiLink href={`mailto:${ref.email.trim()}`}>{ref.email.trim()}</MuiLink>
                                    </ListItem>
                                  ))}
                                </List>
                              ) : (
                                ua?.response || ua?.answer?.answer
                              )}
                            />
                          </ListItem>
                        );
                      }
                      // Default
                      return (
                        <ListItem key={ua.id}>
                          <ListItemText
                            primary={ua?.question?.question}
                            secondary={ua?.response || ua?.answer?.answer}
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </AccordionDetails>
              </Accordion>
            );
          }

          return (
            <Accordion
              key={filterKey}
              expanded={expanded === filterKey}
              onChange={handleChange(filterKey)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: 'primary.light',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                  },
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  {filterKey}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {filteredAnswers.map((ua) => (
                    <ListItem key={ua.id}>
                      <ListItemText
                        primary={ua.question.question}
                        secondary={ua.response || ua.answer?.answer}
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Box>
  );
};

export default DashboardDetails; 